import React, { useState } from "react";
import { HubSpotProperty } from "../types/api";
import { VeracrossField } from "../types/veracrossFields";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  SearchableSelect,
  SearchableSelectOption,
} from "./ui/searchable-select";
import { getHubSpotFieldLabel, getVeracrossFieldLabel } from "../utils/fieldLabels";


interface FieldMappingModalProps {
  hubspotProperties: HubSpotProperty[];
  veracrossFields: VeracrossField[];
  existingMappings?: Array<{
    hubspotField?: string;
    hubspotFieldName?: string;
    veracrossField?: string;
    veracrossFieldName?: string;
  }>;
  onMappingCreated: (mapping: {
    hubspotField: string;
    veracrossField: string;
  }) => void;
  trigger?: React.ReactNode;
}

const FieldMappingModal: React.FC<FieldMappingModalProps> = ({
  hubspotProperties,
  veracrossFields,
  existingMappings = [],
  onMappingCreated,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    hubspotField: "",
    veracrossField: "",
  });

  // Get already mapped field names
  const mappedHubSpotFields = new Set(
    existingMappings.map(mapping => 
      mapping.hubspotFieldName || mapping.hubspotField
    ).filter(Boolean)
  );
  
  const mappedVeracrossFields = new Set(
    existingMappings.map(mapping => 
      mapping.veracrossFieldName || mapping.veracrossField
    ).filter(Boolean)
  );

  // Convert HubSpot properties to searchable select options, filtering out already mapped fields
  const hubspotOptions: SearchableSelectOption[] = hubspotProperties
    .filter(prop => !mappedHubSpotFields.has(prop.name) && !mappedHubSpotFields.has(prop.label))
    .map((prop) => ({
      value: prop.label,
      label: getHubSpotFieldLabel(prop),
    }));

  const veracrossOptions: SearchableSelectOption[] = veracrossFields
    .filter(field => !mappedVeracrossFields.has(field.veracrossFieldName))
    .map((field) => ({
      value: field.veracrossFieldName,
      label: getVeracrossFieldLabel(field),
    }));

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (formData.hubspotField && formData.veracrossField) {
      onMappingCreated(formData);
      setIsOpen(false);
      setFormData({
        hubspotField: "",
        veracrossField: "",
      });
    }
  };

  const isFormValid =
    formData.hubspotField && formData.veracrossField;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || <Button variant="outline">Add Field Mapping</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-w-[95vw]">
        <DialogHeader>
          <DialogTitle>Add Field Mapping</DialogTitle>
          <DialogDescription>
            Configure a new field mapping between HubSpot and Veracross. Select
            the fields below.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4 overflow-hidden">
          <div className="grid gap-2 min-w-0">
            <label htmlFor="hubspotField" className="text-sm font-medium">
              HubSpot Field
            </label>
            <SearchableSelect
              options={hubspotOptions}
              value={formData.hubspotField}
              onValueChange={(value) =>
                handleInputChange("hubspotField", value)
              }
              placeholder="Select HubSpot field"
              searchPlaceholder="Search HubSpot fields..."
              className="w-full min-w-0"
              showClearButton={false}
            />
          </div>

          <div className="grid gap-2 min-w-0">
            <label htmlFor="veracrossField" className="text-sm font-medium">
              Veracross Field
            </label>
            <SearchableSelect
              options={veracrossOptions}
              value={formData.veracrossField}
              onValueChange={(value) =>
                handleInputChange("veracrossField", value)
              }
              placeholder="Select Veracross field"
              searchPlaceholder="Search Veracross fields..."
              className="w-full min-w-0"
              showClearButton={false}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!isFormValid}>
            Add Mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FieldMappingModal;
